import React from 'react';
import styled from 'styled-components';

export interface CardProps {
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  padding?: 'none' | 'small' | 'medium' | 'large';
}

const getPadding = (padding: CardProps['padding'], theme: any) => {
  switch (padding) {
    case 'none':
      return '0';
    case 'small':
      return theme.spacing(1);
    case 'medium':
      return theme.spacing(2);
    case 'large':
      return theme.spacing(3);
    default:
      return theme.spacing(2);
  }
};

const StyledCard = styled.div`
  background-color: ${({ theme }) => theme.colors.background.paper};
  border-radius: ${({ theme }) => theme.shape.borderRadius.large};
  box-shadow: ${({ theme }) => theme.shadows.medium};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const CardHeader = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  border-bottom: 1px solid ${({ theme }) => theme.colors.divider};
`;

const CardTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.h3.fontSize};
  font-weight: ${({ theme }) => theme.typography.h3.fontWeight};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

const CardBody = styled.div<{ padding: CardProps['padding'] }>`
  padding: ${({ theme, padding }) => getPadding(padding, theme)};
  flex-grow: 1; /* Allows body to fill available space */
`;

const CardFooter = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  border-top: 1px solid ${({ theme }) => theme.colors.divider};
  display: flex;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing(1)};
`;

export const Card: React.FC<CardProps> = ({
  title,
  children,
  footer,
  padding = 'medium',
}) => {
  return (
    <StyledCard>
      {title && (
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      )}
      <CardBody padding={padding}>{children}</CardBody>
      {footer && <CardFooter>{footer}</CardFooter>}
    </StyledCard>
  );
};

export default Card; 